import type { CusteioDraft } from "@agroproj/backend/convex/documentDrafts/assemble";

export type SectionChoice = "pending" | "accepted" | "rejected";

export type DraftChoices = {
  basic: SectionChoice;
  report: SectionChoice;
  budget: SectionChoice;
};

export const EMPTY_CHOICES: DraftChoices = {
  basic: "pending",
  report: "pending",
  budget: "pending",
};

const PHASES = ["preparing", "ready", "unsaved", "saved", "stale", "discarded", "error"] as const;

export type DraftPhase = (typeof PHASES)[number];

export const DRAFT_PHASE_LABELS: Record<DraftPhase, string> = {
  preparing: "Preparando",
  ready: "Pronto para conferir",
  unsaved: "Aceito, ainda não salvo",
  saved: "Salvo",
  stale: "Desatualizado",
  discarded: "Descartado",
  error: "Falhou",
};

export const FIELD_STATE_LABELS: Record<CusteioDraft["fields"][number]["state"], string> = {
  source: "Fonte",
  assumption: "Suposição",
  missing: "Ausente",
  conflict: "Conflito",
};

export function acceptanceFromChoices(choices: DraftChoices) {
  return {
    basic: choices.basic === "accepted",
    report: choices.report === "accepted",
    budget: choices.budget === "accepted",
  };
}

/** Warn only after a section was accepted and before Salvar custeio succeeds. */
export function shouldWarnUnsavedDraft(input: { saved: boolean; choices: DraftChoices }) {
  if (input.saved) return false;
  return (
    input.choices.basic === "accepted" ||
    input.choices.report === "accepted" ||
    input.choices.budget === "accepted"
  );
}

export function deriveDraftPhase(input: {
  pending: boolean;
  error: boolean;
  discarded: boolean;
  stale: boolean;
  saved: boolean;
  ready: boolean;
  choices: DraftChoices;
}): DraftPhase | null {
  if (input.pending) return "preparing";
  if (input.error) return "error";
  if (input.discarded) return "discarded";
  if (input.stale) return "stale";
  if (input.saved) return "saved";
  if (shouldWarnUnsavedDraft({ saved: false, choices: input.choices })) return "unsaved";
  if (input.ready) return "ready";
  return null;
}
